import React from 'react';
import { BadgePill } from './BadgePill';
import { useBusinessBadges } from '@/hooks/useBadgeManagement';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export interface BusinessBadgesProps {
  businessId: string;
  mode?: 'compact' | 'full' | 'management';
  maxBadges?: number;
  className?: string;
  showSkeletonOnLoad?: boolean;
}

export const BusinessBadges: React.FC<BusinessBadgesProps> = ({
  businessId,
  mode = 'compact',
  maxBadges,
  className = '',
  showSkeletonOnLoad = true
}) => {
  const { t } = useTranslation();
  const { data: badges, isLoading, error } = useBusinessBadges(businessId);

  // Loading state
  if (isLoading && showSkeletonOnLoad) {
    const skeletonCount = mode === 'compact' ? 2 : 4;
    return (
      <div className={cn('flex flex-wrap gap-2', className)}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Skeleton 
            key={index} 
            className={cn(
              'rounded-full',
              mode === 'compact' ? 'h-6 w-16' : 'h-7 w-20'
            )} 
          />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    console.error('Error loading business badges:', error);
    return null;
  }

  // No badges (check before filtering for better UX)
  if (!badges || badges.length === 0) {
    if (mode === 'management') {
      return (
        <div className={cn('text-center py-4', className)}>
          <p className="text-muted-foreground text-sm">
            {t('badges.no_badges_earned')}
          </p>
        </div>
      );
    }
    return null;
  }

  // Define badges that should be excluded from achievement displays (subscription-based, not merit-based)
  const EXCLUDED_ACHIEVEMENT_BADGE_SLUGS = ['featured'];

  // Filter and sort badges
  const activeBadges = badges.filter(badge => badge.is_active);
  
  // For achievement displays, exclude subscription-based badges
  const achievementBadges = activeBadges.filter(badge => 
    !EXCLUDED_ACHIEVEMENT_BADGE_SLUGS.includes(badge.slug || '')
  );
  
  const sortedBadges = achievementBadges.sort((a, b) => {
    // System badges first, then by assigned date
    if (a.is_system_badge && !b.is_system_badge) return -1;
    if (!a.is_system_badge && b.is_system_badge) return 1;
    return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
  });

  // No achievement badges after filtering
  if (sortedBadges.length === 0) {
    if (mode === 'management') {
      return (
        <div className={cn('text-center py-4', className)}>
          <p className="text-muted-foreground text-sm">
            {t('badges.no_badges_earned')}
          </p>
        </div>
      );
    }
    return null;
  }

  // Limit badges based on mode and maxBadges prop
  const displayLimit = maxBadges || (mode === 'compact' ? 2 : undefined);
  const displayBadges = displayLimit ? sortedBadges.slice(0, displayLimit) : sortedBadges;
  const hasMoreBadges = sortedBadges.length > displayBadges.length;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  // Get translated badge name
  const getBadgeDisplayName = (badge: { translation_key?: string; name: string }) => {
    if (badge.translation_key) {
      const translatedName = t(badge.translation_key, { defaultValue: badge.name });
      return translatedName !== badge.translation_key ? translatedName : badge.name;
    }
    return badge.name;
  };

  // Render badges based on mode
  const renderBadges = () => {
    switch (mode) {
      case 'compact':
        return (
          <div className={cn('flex flex-wrap gap-1', className)}>
            {displayBadges.map((badge, index) => (
              <motion.div
                key={badge.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.05 }}
              >
                <BadgePill
                  badge={{
                    ...badge,
                    icon: badge.icon || 'Star',
                    translatedName: getBadgeDisplayName(badge)
                  }}
                  size="sm"
                  showTooltip={true}
                />
              </motion.div>
            ))}
            {hasMoreBadges && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: displayBadges.length * 0.05 }}
                className="flex items-center"
              >
                <span className="text-xs text-muted-foreground ml-1">
                  +{sortedBadges.length - displayBadges.length}
                </span>
              </motion.div>
            )}
          </div>
        );

      case 'full':
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className={cn('flex flex-wrap gap-2', className)}
          >
            {displayBadges.map((badge, index) => (
              <motion.div
                key={badge.id}
                variants={itemVariants}
              >
                <BadgePill
                  badge={{
                    ...badge,
                    icon: badge.icon || 'Star',
                    translatedName: getBadgeDisplayName(badge)
                  }}
                  size="md"
                  showTooltip={true}
                />
              </motion.div>
            ))}
          </motion.div>
        );

      case 'management':
        return (
          <div className={cn('space-y-4', className)}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {t('badges.earned_badges')} ({sortedBadges.length})
              </h3>
            </div>
            
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-3"
            >
              {displayBadges.map((badge, index) => (
                <motion.div
                  key={badge.id}
                  variants={itemVariants}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors space-y-3"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <BadgePill
                        badge={{
                          ...badge,
                          icon: badge.icon || 'Star',
                          translatedName: getBadgeDisplayName(badge)
                        }}
                        size="md"
                        showTooltip={false}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">
                        {t('badges.earned_on')}: {new Date(badge.assigned_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {badge.description && (
                    <div className="w-full">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {badge.description}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderBadges();
};